import React from 'react';

import type { FileUIPart } from 'ai';

import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Stack } from '@mui/material';

interface ImageThumbnailsProps {
  images: ReadonlyArray<FileUIPart>;
  onRemove?: (index: number) => void;
  expanded?: boolean;
}

function openImagePreview(url: string, alt: string) {
  const preview = window.open('', '_blank');
  if (!preview) {
    return;
  }
  const image = preview.document.createElement('img');
  image.src = url;
  image.alt = alt;
  image.style.maxWidth = '100%';
  image.style.height = 'auto';
  preview.document.body.appendChild(image);
}

export default function ImageThumbnails({ images, onRemove, expanded = false }: ImageThumbnailsProps) {
  if (images.length === 0) {
    return null;
  }
  const removable = Boolean(onRemove);
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={0.75}
      aria-label="Attached images"
      data-testid="ui-stream-image-thumbnails"
      sx={{ mb: 1, justifyContent: removable ? 'flex-start' : 'flex-end', ...(expanded ? { width: '100%' } : {}) }}
    >
      {images.map((image, index) => {
        const alt = `Attached image ${index + 1}`;
        return (
          <Box key={`${index}-${image.url.slice(-24)}`} sx={{ position: 'relative', maxWidth: '100%' }}>
            <Box
              component="img"
              src={image.url}
              alt={alt}
              onClick={removable ? undefined : () => openImagePreview(image.url, alt)}
              sx={{
                display: 'block',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                objectFit: removable ? 'cover' : 'contain',
                ...(removable
                  ? { width: 72, height: 52 }
                  : {
                      maxWidth: expanded ? '100%' : { xs: 160, md: 200 },
                      maxHeight: expanded ? 320 : { xs: 160, md: 200 },
                      cursor: 'zoom-in',
                    }),
              }}
            />
            {onRemove ? (
              <IconButton
                size="small"
                aria-label={`Remove attached image ${index + 1}`}
                onClick={() => onRemove(index)}
                sx={{
                  position: 'absolute',
                  top: -7,
                  right: -7,
                  width: 20,
                  height: 20,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'background.paper' },
                }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            ) : null}
          </Box>
        );
      })}
    </Stack>
  );
}
